import React, { useRef, useState } from "react";
import { Carteirinha } from "./Carteirinha";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toPng, toBlob } from "html-to-image";
import jsPDF from "jspdf";
import { Download, Share2, IdCard, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal que abre uma Carteirinha e permite baixar como PNG ou compartilhar.
 * Usage: <CarteirinhaDialog student={s} stats={{attendance, avgScore, evals}} trigger={<Button/>} />
 */
export function CarteirinhaDialog({ student, stats, trigger, open: openProp, onOpenChange }) {
  const [open, setOpen] = useState(false);
  const ctrl = openProp !== undefined;
  const isOpen = ctrl ? openProp : open;
  const setIsOpen = ctrl ? onOpenChange : setOpen;

  const cardRef = useRef(null);

  const download = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2 });
      const link = document.createElement("a");
      link.download = `carteirinha-${(student?.name || "aluno").replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Carteirinha baixada");
    } catch (err) {
      toast.error("Falha ao gerar imagem");
    }
  };

  const downloadPDF = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 3 });
      // A4 portrait: 210 x 297 mm
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageW = 210, pageH = 297;

      // Header
      pdf.setFillColor(11, 19, 32); // navy
      pdf.rect(0, 0, pageW, 22, "F");
      pdf.setTextColor(166, 230, 53); // lime
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text("CARTEIRA DE ATLETA", 12, 10);
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.text("VOLEI VIDA — Jardinopolis", 12, 17);

      // Card image — keep aspect ratio (card is 400x700)
      const cardAspect = 700 / 400;
      const cardW = 110;                    // mm
      const cardH = cardW * cardAspect;     // ≈ 192.5 mm
      const x = (pageW - cardW) / 2;
      const y = 35;
      pdf.addImage(dataUrl, "PNG", x, y, cardW, cardH, undefined, "FAST");

      // Footer
      pdf.setTextColor(120, 120, 120);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      const today = new Date().toLocaleDateString("pt-BR");
      pdf.text(`Emitido em ${today} • ID ${student?.id?.slice(0, 8).toUpperCase() || "—"}`, pageW / 2, pageH - 10, { align: "center" });

      const fileName = `carteirinha-${(student?.name || "aluno").replace(/\s+/g, "_")}.pdf`;
      pdf.save(fileName);
      toast.success("PDF baixado");
    } catch (err) {
      toast.error("Falha ao gerar PDF");
    }
  };

  const share = async () => {
    if (!cardRef.current) return;
    try {
      const blob = await toBlob(cardRef.current, { cacheBust: true, pixelRatio: 2 });
      if (!blob) throw new Error("blob null");
      const file = new File([blob], `carteirinha-${student?.name || "aluno"}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Carteirinha de ${student?.name}`,
          text: `Vôlei Vida — ${student?.name} (${student?.category})`,
        });
      } else {
        // Fallback: copy to clipboard if supported, else download
        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          toast.success("Imagem copiada! Cole no WhatsApp ou Instagram.");
        } else {
          await download();
        }
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Compartilhamento não suportado neste navegador");
    }
  };

  return (
    <>
      {trigger && <span onClick={() => setIsOpen(true)}>{trigger}</span>}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="carteirinha-dialog">
          <DialogHeader>
            <DialogTitle>Carteirinha do atleta</DialogTitle>
            <DialogDescription>
              Baixe como PNG, PDF para impressão ou compartilhe.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <Carteirinha ref={cardRef} student={student} stats={stats} />
          </div>
          <div className="flex flex-wrap justify-center gap-2 pt-2 border-t border-border">
            <Button onClick={download} data-testid="carteirinha-download-button" className="bg-[hsl(var(--vv-blue))] font-bold">
              <Download className="size-4 mr-2" /> Baixar PNG
            </Button>
            <Button onClick={downloadPDF} data-testid="carteirinha-pdf-button" className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold hover:bg-[hsl(var(--vv-lime)/0.85)]">
              <FileText className="size-4 mr-2" /> Baixar PDF
            </Button>
            <Button onClick={share} variant="outline" data-testid="carteirinha-share-button">
              <Share2 className="size-4 mr-2" /> Compartilhar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function CarteirinhaButton(props) {
  return (
    <CarteirinhaDialog
      {...props}
      trigger={
        <Button variant="outline" className="border-[hsl(var(--vv-lime))/0.5] text-[hsl(var(--vv-lime))] hover:bg-[hsl(var(--vv-lime))] hover:text-[hsl(218_50%_10%)] font-bold uppercase tracking-wider" data-testid="open-carteirinha-button">
          <IdCard className="size-4 mr-2" /> Ver carteirinha
        </Button>
      }
    />
  );
}
